// packages/api/src/hooks/admin/usePricingAdjustments.ts
// Phase 02.3 P07 — Admin Pricing Adjustments hooks (matriz 4D + CSV import + simulator).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiSuccess, ChangeSetActiveEnrichment } from '@alamo/types';

import { apiClient } from '../../client';
import { pricingChangeSetKeys } from '../../query-keys';

// ─── Types (inline; promote to @alamo/types if shared with backend) ────────

export type AdjustmentScopeType = 'region' | 'vehicle_attribute' | 'customer_tier' | 'promotion';

/**
 * ESCR-184.1 — backend aceita 'percent' como alias canonico de 'percentage'
 * (CHECK pa_adjustment_type_check expandido). Frontend envia 'percentage' por
 * compat; recebe ambos.
 */
export type AdjustmentType = 'percentage' | 'percent' | 'fixed';

/**
 * XXX3.1/XXX3.3 (Geografia v4 Wave C) — como o motor de pricing deve tratar o
 * adjustment ao bater na cotacao:
 *   - 'apply'   (default legado): multiplica/soma o adjustmentValue
 *   - 'ignore': no-op preservado no breakdown (auditavel)
 *   - 'replace': substitui factor_base pela zona em replacementZoneId
 *
 * Quando targetAction='replace', replacementZoneId e OBRIGATORIO (validado por
 * class-validator + CHECK no DB).
 */
export const ADJUSTMENT_TARGET_ACTIONS = ['apply', 'ignore', 'replace'] as const;
export type AdjustmentTargetAction = (typeof ADJUSTMENT_TARGET_ACTIONS)[number];

/** ESCR-184.1 — canais aceitos em `channels[]` (mesmo enum de acceptance_rules). */
export type AdjustmentChannel = 'consultant' | 'self_service' | 'public_link';

/** ESCR-184.1 — tipos de cotacao aceitos em `quotation_types[]`. */
export type AdjustmentQuotationType = 'new_zero_km' | 'used' | 'renewal' | 'fleet';

/** ESCR-184.1 — combustiveis aceitos em `fuel_types[]`. */
export type AdjustmentFuelType = 'gasoline' | 'diesel' | 'flex' | 'hybrid' | 'electric';

/**
 * Perfil de uso do veiculo (escalar). `null`/ausente = vale para qualquer uso
 * (default). Vocabulario do motor de pricing; `uber_app` da cotacao colapsa em
 * `comercial` no lookup do backend.
 */
export type AdjustmentUsageProfile = 'particular' | 'comercial';
export type AdjustmentCalculationBase = 'base_protection' | 'after_adjustments';
export type AdjustmentDirection = 'discount' | 'surcharge' | 'neutral';
export type AdjustmentZoneLevel =
  | 'region'
  | 'state'
  | 'microregion'
  | 'metropolitan_region'
  | 'interior';

export interface PricingAdjustment {
  id: string;
  name: string;
  description: string | null;
  /** @deprecated ESCR-184.1 — substituido por criterios tipados; backend mantem por compat. */
  scopeType: AdjustmentScopeType;
  /** @deprecated ESCR-184.1 — geografia agora vive em `zones` (refactor 0040). */
  stateId: string | null;
  /** @deprecated idem stateId. */
  cityId: string | null;
  /** @deprecated idem stateId. */
  neighborhoodId: string | null;
  /** @deprecated ESCR-184.1 — preferir `categoryIds[]`. */
  categoryId: string | null;
  /** @deprecated ESCR-179.9 — preferir `subgroupIds[]`. */
  subgroupId: string | null;
  // ESCR-184.1 — criterios tipados (16 colunas, todas opcionais; null = sem filtro)
  /** Veiculo: categorias aceitas. Vazio/null = qualquer categoria. */
  categoryIds: string[] | null;
  /** Veiculo: subgrupos aceitos (filtrados pela categoria selecionada). */
  subgroupIds: string[] | null;
  /** Veiculo: modelos especificos (vehicle_models.id). */
  modelIds: string[] | null;
  yearMin: number | null;
  yearMax: number | null;
  /** Decimal serializado como string (DECIMAL no backend). */
  fipeMin: string | null;
  fipeMax: string | null;
  fuelTypes: AdjustmentFuelType[] | null;
  kmMin: number | null;
  kmMax: number | null;
  /** Veiculo: perfil de uso. null = qualquer uso (sem filtro). */
  usageProfile: AdjustmentUsageProfile | null;
  /**
   * Plano ao qual o ajuste se aplica (FK escalar `plans.id`). `null` = vale para
   * TODOS os planos (catch-all). Contrato ESCR-pricing-adjustment-plan-id.
   */
  planId: string | null;
  quotationTypes: AdjustmentQuotationType[] | null;
  channels: AdjustmentChannel[] | null;
  /** @deprecated ESCR-184.15 — coluna `plan_ids` DROPADA; backend nunca popula. Usar `planId` (FK escalar). */
  planIds: string[] | null;
  driverAgeMin: number | null;
  driverAgeMax: number | null;
  /** Tempo minimo de carteira (CNH) em meses. */
  driverTenureMonthsMin: number | null;

  adjustmentType: AdjustmentType;
  adjustmentValue: string;
  calculationBase?: AdjustmentCalculationBase;
  validFrom: string;
  validTo: string | null;
  /** @deprecated ESCR-184.5 — Pilar 3 nao usa prioridade (regras sao SOMADAS, nao priorizadas). */
  priority: number;
  deletedAt: string | null;
  deletedBy: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;

  /**
   * YYY2.1 (Pilar 3 → Geografia cordão umbilical reverso) — backend embeda
   * via LEFT JOIN zones em `list()` quando `zoneId IS NOT NULL`. Null caso
   * contrário OU quando a zona foi deletada (ON DELETE SET NULL na FK).
   */
  zoneSummary?: ZoneSummary | null;

  /** Phase 08 Plan 06 (ESCR-218) — enrichment CS ativo vinculado ao ajuste (Plan 01 backend). */
  activeChangeSet?: ChangeSetActiveEnrichment | null;

  // Contrato ESCR-geo-panel-adjustments — nomes resolvidos das dimensões
  // (LEFT JOIN + array_agg no backend, sem N+1). Convenção UI: nunca exibir
  // UUID; usar estes nomes. `null` quando a FK/array correspondente é null.
  /** Resolve `categoryId` (escalar). */
  categoryName?: string | null;
  /** Resolve `subgroupId` (escalar). */
  subgroupName?: string | null;
  /** Resolve `planId` (escalar). */
  planName?: string | null;
  /** Resolve `categoryIds[]` (ordenado por nome asc). */
  categoryNames?: string[] | null;
  /** Resolve `subgroupIds[]` (ordenado por nome asc). */
  subgroupNames?: string[] | null;
}

/** YYY2.1 — Metadata mínima embedada em rules ligadas a uma zona. */
export interface ZoneSummary {
  id: string;
  code: string;
  name: string;
  level: string;
  color: string;
}

export interface AdjustmentCounts {
  factors: number;
  additives: number;
}

export interface UpsertAdjustmentInput {
  name: string;
  description?: string;
  /** @deprecated ESCR-184.1 — backend mantem por compat; novos criterios via campos tipados. */
  scopeType: AdjustmentScopeType;
  /** @deprecated ESCR-184.1 — preferir `categoryIds[]`. */
  categoryId?: string | null;
  /** @deprecated ESCR-179.9 — preferir `subgroupIds[]`. */
  subgroupId?: string | null;
  // ESCR-184.1 — criterios tipados (todos opcionais; null/[] = sem filtro)
  categoryIds?: string[] | null;
  subgroupIds?: string[] | null;
  modelIds?: string[] | null;
  yearMin?: number | null;
  yearMax?: number | null;
  fipeMin?: string | null;
  fipeMax?: string | null;
  fuelTypes?: AdjustmentFuelType[] | null;
  kmMin?: number | null;
  kmMax?: number | null;
  /**
   * Perfil de uso (escalar). null/ausente = qualquer uso. O PUT e full-replace:
   * reenviar sempre (omitir apaga a dimensao de um ajuste ja publicado).
   */
  usageProfile?: AdjustmentUsageProfile | null;
  /**
   * Plano (FK escalar `plans.id`). null/ausente = TODOS os planos. O PUT e
   * full-replace: reenviar sempre (omitir apaga o vinculo de um ajuste ja
   * publicado). Contrato ESCR-pricing-adjustment-plan-id.
   */
  planId?: string | null;
  quotationTypes?: AdjustmentQuotationType[] | null;
  channels?: AdjustmentChannel[] | null;
  /** @deprecated ESCR-184.15 — coluna `plan_ids` DROPADA; nunca enviada. Usar `planId`. */
  planIds?: string[] | null;
  driverAgeMin?: number | null;
  driverAgeMax?: number | null;
  driverTenureMonthsMin?: number | null;

  adjustmentType: AdjustmentType;
  adjustmentValue: string;
  calculationBase?: AdjustmentCalculationBase;
  validFrom?: string;
  validTo?: string | null;
  /** @deprecated ESCR-184.5 — Pilar 3 nao usa prioridade. */
  priority?: number;

  // XXX3.3 (Geografia v4) — refactor zone-aware
  /**
   * FK zones.id da zona alvo. Obrigatorio quando scopeType='region'.
   * No PUT: omitir PRESERVA a zona atual; `null` explicito desvincula.
   */
  zoneId?: string | null;
  /** Como o motor trata este ajuste; default 'apply' (legado). Omitir no PUT preserva. */
  targetAction?: AdjustmentTargetAction;
  /**
   * FK zones.id da zona substituta. Obrigatorio quando targetAction='replace'.
   * No PUT: omitir PRESERVA; `null` explicito desvincula.
   */
  replacementZoneId?: string | null;
}

export interface ListAdjustmentsFilters {
  scopeType?: AdjustmentScopeType;
  adjustmentType?: AdjustmentType;
  stateId?: string;
  cityId?: string;
  /** XXX3 (Geografia) — filtra por zona alvo exata (`pricing_adjustments.zone_id`). */
  zoneId?: string;
  /** Filtro explícito: categoria escalar ou array correspondente; NULL não casa. */
  categoryId?: string;
  /** Filtro explícito: subgrupo escalar ou array correspondente; NULL não casa. */
  subgroupId?: string;
  /** Filtro explícito por perfil de uso; NULL não casa quando selecionado. */
  usageProfile?: AdjustmentUsageProfile;
  direction?: AdjustmentDirection;
  zoneLevel?: AdjustmentZoneLevel;
  /** Filtro explícito por plano (FK `plans.id`); NULL não casa quando selecionado. */
  planId?: string;
  status?: string;
  /** YYY1.4 — `pilar3` → zone_id IS NULL; `geografia` → zone_id NOT NULL; `all` (default) sem filtro. */
  source?: 'all' | 'pilar3' | 'geografia';
  /**
   * Phase 08.3 Plan 02 (paridade Pilar 1/2). Filtro por status do change-set
   * ativo vinculado ao ajuste — backend faz EXISTS em change_set_items quando
   * fornecido. Quando ausente, lista todos.
   */
  csStatus?: 'draft' | 'approval_pending';
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface PaginatedAdjustments {
  items: PricingAdjustment[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface ImportPreviewRow {
  rowNumber: number;
  label: string;
  type: AdjustmentType;
  scopeType: string;
  previous: string | null;
  next: string;
  status: 'new' | 'changed' | 'same';
}

export interface ImportRowError {
  rowNumber: number;
  column?: string;
  code: string;
  message: string;
}

export interface ImportSummary {
  ok: number;
  fail: number;
  discarded: number;
  discardedRowNumbers: number[];
  total: number;
}

export interface ImportDryRunResponse {
  dryRun: true;
  filename: string;
  validRows: ImportPreviewRow[];
  errors: ImportRowError[];
  summary: ImportSummary;
}

export interface ImportCommitResponse {
  dryRun: false;
  filename: string;
  imported: number;
  errors: ImportRowError[];
  summary: ImportSummary;
}

export interface ImportAsyncJobResponse {
  asyncJobId: string;
  status: 'queued';
  filename: string;
  total: number;
}

export type ImportResponse = ImportDryRunResponse | ImportCommitResponse | ImportAsyncJobResponse;

export interface SimulateAdjustmentInput {
  subgroupId: string;
  profileCode?: string;
  locationCode?: string;
}

export interface SimulateAdjustmentResponse {
  current: { finalPrice: number; calculationMemory: Array<{ step: string; value: number }> };
  simulated: { finalPrice: number; calculationMemory: Array<{ step: string; value: number }> };
  deltaPercent: number;
  stub: true;
}

// ─── Query keys ────────────────────────────────────────────────────────────

export const pricingAdjustmentsKeys = {
  all: ['admin', 'pricing-adjustments'] as const,
  counts: () => [...pricingAdjustmentsKeys.all, 'counts'] as const,
  list: (filters: ListAdjustmentsFilters) =>
    [...pricingAdjustmentsKeys.all, 'list', filters] as const,
  detail: (id: string) => [...pricingAdjustmentsKeys.all, 'detail', id] as const,
  /** YYY2.2/2.4 — KPI cobertura por período. */
  coverage: (period: CoveragePeriod) =>
    [...pricingAdjustmentsKeys.all, 'kpis', 'coverage', period] as const,
};

const BASE = '/admin/pricing-adjustments';

// ─── Counts (tabs feed) ────────────────────────────────────────────────────

export function useAdjustmentCounts() {
  return useQuery({
    queryKey: pricingAdjustmentsKeys.counts(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<AdjustmentCounts>>(`${BASE}/counts`);
      return res.data.data;
    },
  });
}

// ─── YYY2.4 — KPI cobertura (% cotações com ajuste por período) ───────────

export type CoveragePeriod = '24h' | '7d' | '30d';

export interface AdjustmentsCoverage {
  period: CoveragePeriod;
  totalQuotations: number;
  withAdjustment: number;
  /** DECIMAL string com 2 casas (ex: "12.50"). */
  percentApplied: string;
  /** DECIMAL string em BRL com 2 casas (média absoluta das linhas adjustment). */
  avgAdjustmentValueBrl: string;
}

export function usePricingAdjustmentsCoverage(period: CoveragePeriod = '24h') {
  return useQuery({
    queryKey: pricingAdjustmentsKeys.coverage(period),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<AdjustmentsCoverage>>(`${BASE}/kpis/coverage`, {
        params: { period },
      });
      return res.data.data;
    },
    // Cache 60s — KPI muda devagar; refetch implícito ao trocar período.
    staleTime: 60_000,
  });
}

// ─── List ──────────────────────────────────────────────────────────────────

export function useAdjustments(
  filters: ListAdjustmentsFilters = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: pricingAdjustmentsKeys.list(filters),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PaginatedAdjustments>>(BASE, {
        params: filters,
      });
      return res.data.data;
    },
    enabled: options?.enabled ?? true,
  });
}

// ─── Detail ────────────────────────────────────────────────────────────────

export function useAdjustment(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? pricingAdjustmentsKeys.detail(id) : ['pricing-adjustment-disabled'],
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PricingAdjustment>>(`${BASE}/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

// ─── Create ────────────────────────────────────────────────────────────────

export function useCreateAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertAdjustmentInput) => {
      const res = await apiClient.post<ApiSuccess<PricingAdjustment>>(BASE, input);
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdjustmentsKeys.all });
      // Phase 08.3 Plan 03 — Pilar 3 muta entry pode criar/atualizar item no
      // CS ativo (auto-attach quando publicada). Invalida CS lookups para
      // refletir badges + indicadores antes→depois sem refetch manual.
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

// ─── Update ────────────────────────────────────────────────────────────────

export function useUpdateAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; data: UpsertAdjustmentInput }) => {
      const res = await apiClient.put<ApiSuccess<PricingAdjustment>>(
        `${BASE}/${input.id}`,
        input.data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdjustmentsKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

// ─── Delete (soft) ─────────────────────────────────────────────────────────

export function useDeleteAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await apiClient.delete(`${BASE}/${id}`, { data: { reason } });
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdjustmentsKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

/**
 * Submit do entry (draft|rejected → approval_pending). Distinto do
 * `submitCs(csId)`, que opera sobre o change-set quando a regra ja era
 * publicada e teve edicao. Aqui o submit eh do PROPRIO ajuste novo.
 */
export function useSubmitAdjustmentEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`${BASE}/${id}/submit`);
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdjustmentsKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

/**
 * Approve do entry diretamente. Backend `approve` (runTransition) ja
 * transiciona o entry de approval_pending direto pra published numa unica
 * chamada — nao chamar /publish depois (gera INVALID_STATUS_TRANSITION).
 */
export function useApproveAdjustmentEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`${BASE}/${id}/approve`);
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdjustmentsKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

/** Reject do entry diretamente. Aceita motivo opcional. */
export function useRejectAdjustmentEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      await apiClient.post(`${BASE}/${id}/reject`, reason ? { reason } : undefined);
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdjustmentsKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

// ─── Import (CSV/XLSX) ─────────────────────────────────────────────────────

export function useImportAdjustments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; dryRun: boolean }) => {
      const fd = new FormData();
      fd.append('file', input.file);
      const res = await apiClient.post<ApiSuccess<ImportResponse>>(
        `${BASE}/import?dryRun=${input.dryRun}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return res.data.data;
    },
    onSuccess: (data) => {
      // Apenas invalida cache se foi commit real (não dry-run)
      if ('imported' in data) {
        qc.invalidateQueries({ queryKey: pricingAdjustmentsKeys.all });
        qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
      }
    },
  });
}

// ─── Simulate (STUB; P10 substitui backend) ────────────────────────────────

export function useSimulateAdjustment() {
  return useMutation({
    mutationFn: async (input: SimulateAdjustmentInput) => {
      const res = await apiClient.post<ApiSuccess<SimulateAdjustmentResponse>>(
        `${BASE}/simulate`,
        input,
      );
      return res.data.data;
    },
  });
}
